import {
  CLEAR_RENDER_API_POLL_TIMEOUT,
  SET_DESIGN_TO_PLAYGROUND,
  SET_RENDER_API_POLL_TIMEOUT_ID,
  SET_DESIGN_TO_CAMERA,
  UPDATE_SWAP_END_RESULTS,
  UPDATE_SHOW_SWAP_NOTIFICATION,
  UPDATE_SHOW_SWAP_MODAL_STATUS,
  UPDATE_SWAP_STATUS
} from './action-constants';

import {
  clearRenderApiPollTimeout,
  updateSwapEndResults,
  updateDesignToPlayground,
  updateDesignToCamera,
  updateRenderApiPollTimeoutId,
  updateSwapStatuses
} from './utils';

import { Replacement, SwapService } from 'services/LiveSwap';

/*
When a swap is initiated, we will store the design as a key in designSwappingStatuses.
The value will be an object with hotspot id as key and product id as value.

When the swap is complete, we will empty the design object in designSwappingStatuses.
If there are no values in the design's object, we no nothing is happening with the design.

Also when the swap is complete, we store it in completedSwaps object.
Design and hotspot ids are the key and a bool is the value.
An additional completed object is needed so we know if the swap was completed,
and not cancelled. TODO: Could just store a string status in the designSwappingStatuses
object and just use one.

We store a dict for design id to the playground studio, so that we may
redirect to studio from parent and cancel all swap activity from parent as well.

When polling for a renders status, we set an intial timeout for 15s then 5s after that.
We store the timeout ids in renderApiPolls with design (always a playground design id)
as key. We can clear these timeouts to stop polling for the render.
*/
const initialState = {
  designSwappingStatuses: {}, // will have design id as key; { hotspotid: productid } as value
  designToPlaygroundDict: {}, // will have design id as key and playground id as value
  swapEndResults: {}, // design & hotspot instance id & product id as key and success bool as value
  designToCameraDict: {}, // design id as key and camera name as value
  renderApiPolls: {}, // will have design instance id as key and timeout id as value
  showSwapNotification: false, // located in lower left corner
  swapNotificationInfo: {},
  swapNotificationDesign: null,
  showSwapModal: false,
  swapModalInfo: {},

  // Swap data.
  swapReplacement: new Replacement({}),
  swapRendersInProgress: {}
};

export const reducerKey = 'swapProducts';

export const reducer = (state = initialState, action) => {
  switch (action.type) {
    case CLEAR_RENDER_API_POLL_TIMEOUT:
      return clearRenderApiPollTimeout(state, action.payload);

    case SET_DESIGN_TO_PLAYGROUND:
      return updateDesignToPlayground(state, action.payload);

    case SET_DESIGN_TO_CAMERA:
      return updateDesignToCamera(state, action.payload);

    case SET_RENDER_API_POLL_TIMEOUT_ID:
      return updateRenderApiPollTimeoutId(state, action.payload);

    case UPDATE_SWAP_END_RESULTS:
      return updateSwapEndResults(state, action.payload);

    case UPDATE_SHOW_SWAP_MODAL_STATUS: {
      const { parentDesignId, newDesignId, cameraId, status } = action.payload;

      return {
        ...state,
        showSwapModal: status,
        swapModalInfo: { parentDesignId, newDesignId, cameraId }
      };
    }

    case UPDATE_SHOW_SWAP_NOTIFICATION: {
      const {
        cameraId,
        design,
        messageType,
        productIds,
        renderUid,
        status
      } = action.payload;

      const newState = {
        ...state,
        showSwapNotification: status
      };

      newState.swapNotificationInfo = status
        ? { cameraId, messageType, productIds, renderUid }
        : initialState.swapNotificationInfo;

      // design could be written over before user can use link to go to design if
      // in notification info object
      if (design) { newState.swapNotificationDesign = design; }

      return newState;
    }

    case UPDATE_SWAP_STATUS:
      return updateSwapStatuses(state, action.payload);

    case SwapService.REPLACEMENT_DATA_UPDATED:
      return { ...state, swapReplacement: action.payload };

    case SwapService.RENDER_IN_PROGRESS_UPDATED: {
      const renderInProgress = action.payload;
      return {
        ...state,
        swapRendersInProgress: {
          ...state.swapRendersInProgress,
          [renderInProgress.renderUid()]: renderInProgress
        }
      };
    }

    case SwapService.RENDERS_IN_PROGRESS_UPDATED: {
      const rendersInProgress = action.payload;
      return {
        ...state,
        swapRendersInProgress: rendersInProgress.reduce((accum, render) => {
          // Update renders in progress in state given from payload.
          // NOTE: used as a key to ensure same render is not refetched
          // multiple times
          const renderUid = render && render.renderUid();
          if (renderUid) {
            return {
              ...accum,
              [renderUid]: render
            };
          }
          return accum;
        }, state.swapRendersInProgress)
      };
    }

    default:
      return state;
  }
};
